import { supabase, PhotoReviewItem, ModerationStats } from '../lib/supabase'

// Demo data for when Supabase is not configured
const demoPhotoReviews: PhotoReviewItem[] = [
  {
    moderation_id: '1',
    user_id: 'user-1',
    photo_url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
    submitted_at: new Date().toISOString(),
    user_name: 'John Doe',
    user_email: 'john.doe@example.com',
    age: 28,
    gender: 'Male',
    mobile: '+91-9876543210',
    current_photo_url: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face'
  },
  {
    moderation_id: '2',
    user_id: 'user-2',
    photo_url: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
    submitted_at: new Date(Date.now() - 3600000).toISOString(),
    user_name: 'Jane Smith',
    user_email: 'jane.smith@example.com',
    age: 25,
    gender: 'Female',
    mobile: '+91-9876543211',
    current_photo_url: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face'
  },
  {
    moderation_id: '3',
    user_id: 'user-3',
    photo_url: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face',
    submitted_at: new Date(Date.now() - 7200000).toISOString(),
    user_name: 'Mike Johnson',
    user_email: 'mike.johnson@example.com',
    age: 32,
    gender: 'Male',
    mobile: '+91-9876543212',
    current_photo_url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face'
  }
]

const demoStats: ModerationStats = {
  pending_count: 3,
  approved_count: 15,
  rejected_count: 2,
  approved_today: 5,
  rejected_today: 1,
  avg_review_time_minutes: 12
}

// Check if we're in demo mode
const isDemoMode = !process.env.REACT_APP_SUPABASE_URL || 
                   process.env.REACT_APP_SUPABASE_URL === 'https://placeholder.supabase.co'

// Debug function to check configuration
export const debugConfig = () => {
  console.log('=== Photo Moderation Service Debug ===')
  console.log('Supabase URL:', process.env.REACT_APP_SUPABASE_URL)
  console.log('Supabase Key:', process.env.REACT_APP_SUPABASE_ANON_KEY ? 'Present' : 'Missing')
  console.log('Demo Mode:', isDemoMode)
  console.log('=====================================')
}

// Predefined rejection reasons
export const REJECTION_REASONS = [
  'Inappropriate content',
  'Poor quality image',
  'Not a real person',
  'Face not clearly visible',
  'Violates community guidelines',
  'Other'
]

export const photoModerationService = {
  // Get all pending profile pictures for review
  async getPendingPhotos(): Promise<PhotoReviewItem[]> {
    if (isDemoMode) {
      // Return demo data with a small delay to simulate API call
      await new Promise(resolve => setTimeout(resolve, 500))
      return [...demoPhotoReviews]
    }

    try {
      const { data, error } = await supabase.rpc('get_pending_profile_photos')
      
      if (error) {
        console.error('Error fetching pending photos:', error)
        throw error
      }

      return data || []
    } catch (error) {
      // Fallback to direct query if RPC function doesn't exist
      const { data, error: queryError } = await supabase
        .from('profile_photo_moderation')
        .select(`
          id,
          user_id,
          photo_url,
          created_at,
          profiles:user_id (
            name,
            email,
            age,
            gender,
            mobile,
            photo_url
          )
        `)
        .eq('status', 'pending')
        .order('created_at', { ascending: true })

      if (queryError) {
        throw queryError
      }

      // Transform the data to match our interface
      return data?.map((item: any) => ({
        moderation_id: item.id,
        user_id: item.user_id,
        photo_url: item.photo_url,
        submitted_at: item.created_at,
        user_name: item.profiles?.name || 'Unknown',
        user_email: item.profiles?.email || '',
        age: item.profiles?.age,
        gender: item.profiles?.gender,
        mobile: item.profiles?.mobile || '',
        current_photo_url: item.profiles?.photo_url
      })) || []
    }
  },

  // Approve a profile picture
  async approvePhoto(moderationId: string, adminId: string): Promise<{ success: boolean; message: string; old_photo_url?: string }> {
    if (isDemoMode) {
      // Simulate approval in demo mode
      await new Promise(resolve => setTimeout(resolve, 1000))
      return {
        success: true,
        message: 'Profile picture approved successfully',
        old_photo_url: 'https://example.com/old-photo.jpg'
      }
    }

    try {
      const { data, error } = await supabase.rpc('approve_profile_picture', {
        p_moderation_id: moderationId,
        p_admin_id: adminId
      })

      if (error) {
        console.error('RPC function error:', error)
        throw new Error(`Database function error: ${error.message}`)
      }

      return data || { success: false, message: 'Unknown error occurred' }
    } catch (error) {
      console.error('Error approving photo:', error)
      // Return a more user-friendly error message
      if (error instanceof Error) {
        throw new Error(`Failed to approve photo: ${error.message}`)
      } else {
        throw new Error('Failed to approve photo: Unknown error occurred')
      }
    }
  },

  // Reject a profile picture
  async rejectPhoto(moderationId: string, adminId: string, reason: string): Promise<{ success: boolean; message: string; photo_url_to_delete?: string }> {
    if (isDemoMode) {
      // Simulate rejection in demo mode
      await new Promise(resolve => setTimeout(resolve, 1000))
      return {
        success: true,
        message: 'Profile picture rejected successfully',
        photo_url_to_delete: 'https://example.com/rejected-photo.jpg'
      }
    }

    try {
      const { data, error } = await supabase.rpc('reject_profile_picture', {
        p_moderation_id: moderationId,
        p_admin_id: adminId,
        p_rejection_reason: reason
      })

      if (error) {
        console.error('RPC function error:', error)
        throw new Error(`Database function error: ${error.message}`)
      }

      return data || { success: false, message: 'Unknown error occurred' }
    } catch (error) {
      console.error('Error rejecting photo:', error)
      // Return a more user-friendly error message
      if (error instanceof Error) {
        throw new Error(`Failed to reject photo: ${error.message}`)
      } else {
        throw new Error('Failed to reject photo: Unknown error occurred')
      }
    }
  },

  // Get moderation statistics
  async getModerationStats(): Promise<ModerationStats> {
    if (isDemoMode) {
      await new Promise(resolve => setTimeout(resolve, 300))
      return { ...demoStats }
    }

    try {
      const { data, error } = await supabase.rpc('get_moderation_statistics')
      
      if (error) {
        console.error('Error fetching moderation stats:', error)
        throw error
      }

      return data || demoStats
    } catch (error) {
      // Fallback to direct queries if RPC function doesn't exist
      try {
        const [pendingResult, approvedResult, rejectedResult] = await Promise.all([
          supabase
            .from('profile_photo_moderation')
            .select('id', { count: 'exact' })
            .eq('status', 'pending'),
          supabase
            .from('profile_photo_moderation')
            .select('id', { count: 'exact' })
            .eq('status', 'approved'),
          supabase
            .from('profile_photo_moderation')
            .select('id', { count: 'exact' })
            .eq('status', 'rejected')
        ])

        return {
          pending_count: pendingResult.count || 0,
          approved_count: approvedResult.count || 0,
          rejected_count: rejectedResult.count || 0,
          approved_today: 0, // Would need more complex query
          rejected_today: 0, // Would need more complex query
          avg_review_time_minutes: 0 // Would need more complex query
        }
      } catch (fallbackError) {
        console.error('Error in fallback stats query:', fallbackError)
        return demoStats
      }
    }
  },

  // Delete image from Supabase Storage
  async deleteImageFromStorage(imageUrl: string): Promise<boolean> {
    if (isDemoMode) {
      // Simulate deletion in demo mode
      await new Promise(resolve => setTimeout(resolve, 500))
      return true
    }

    try {
      // Extract file path from URL
      const urlParts = imageUrl.split('/')
      const fileName = urlParts[urlParts.length - 1]
      const userId = urlParts[urlParts.length - 3]
      const imageType = urlParts[urlParts.length - 2]
      const filePath = `${userId}/${imageType}/${fileName}`
      
      const { error } = await supabase.storage
        .from('user-images')
        .remove([filePath])
      
      if (error) {
        console.error('Error deleting image:', error)
        return false
      }
      
      return true
    } catch (error) {
      console.error('Error:', error)
      return false
    }
  }
}
